import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, CheckCircle2, Keyboard, Link2, Loader2, Minus, PackagePlus, Plus, RotateCcw, ScanBarcode, Search, Smartphone, Trash2, X } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { FeedbackActions, Scanner, type ScanFeedback } from "@/components/scanner/Scanner"
import { notASerial } from "@/components/scanner/classify"
import {
  CHOICE_LABEL,
  NON_GTIN_WARNING,
  inSerialMode,
  isOwnCode,
  notSerialAction,
  offerNotSerial,
  saveAsBarcode,
  serialModeChoices,
  withoutAssociation,
  type SaveBarcodeDeps,
  type SerialChoice,
} from "./serial-choices"
import { RequestError } from "./request-error"
import { SerialActivationDialog, type SerialActivationAsk } from "./SerialActivationDialog"
import { ConfirmDialog } from "./ConfirmDialog"
import {
  RONDA_STATUS,
  activateRondaSerial,
  associateCode,
  confirmReplacePending,
  fmtQty,
  getRonda,
  lineResultLabel,
  num,
  patchRonda,
  replaceConfirmText,
  reviewNotices,
  reviewRonda,
  scanRonda,
  replaceRondaProduct,
  searchRondaProducts,
  sendRonda,
  signed,
  unitLabel,
  type PickProduct,
  type Review,
  type ReviewItem,
  type RondaDetail as Detail,
  type RondaLine,
  type RondaProduct,
  type ScanOutcome,
} from "./ronda-api"
import { ActionBar, ActionBarSpacer, BottomSheet } from "./ui"
import { REPLACE_TOAST, bareName, canReplaceProduct, replaceFeedback, replaceRequestFor } from "./replace-product"
import { confirmDialog } from "@/lib/confirm"

const hhmm = (iso: string) => new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })

interface Current {
  id: number
  name: string | null
  default_code: string | null
  tracking: string | null
  uom: string | null
  /** «Ler séries» num artigo sem rastreio (0.35.0): a 1.ª série pede para ativar o número de série no Odoo. */
  asSerial?: boolean
  /** O que o Odoo tem aqui, vindo da pesquisa (antes de haver contagem do artigo); fora da cega. */
  odooQty?: number
  odooReserved?: number
}

const WORKING = ["aberta", "por_rever", "aplicada_parcial"]

/** Uma resposta nas «recentes» do ecrã (o `id` mantém-se quando uma escolha a substitui). */
interface RecentEntry {
  id: number
  fb: ScanFeedback
}

/** Código desconhecido à espera de «Associar a um artigo». */
interface PendingCode {
  code: string
  /** Não é um EAN/GTIN: associa-se só depois do aviso (pode ser a série desta caixa). */
  nonGtin: boolean
}

/**
 * Uma ronda — passos 2 a 4 do mockup (`docs/ronda.mockup.html`), no mesmo ecrã:
 *   2. **Ler** — um só campo: EAN, caixa, etiqueta com a referência, série
 *      (uma série conhecida descobre o artigo); código desconhecido →
 *      «Associar a um artigo»; escrever a quantidade.
 *   3. **Rever** — contado × Odoo agora: igual, séries para o stock antigo,
 *      diferenças, «vão a zero», por resolver; «Voltar a contar» um artigo.
 *   4. **Resumo e enviar** — o que vai acontecer; «Enviar» aplica no Odoo.
 * Depois: «Aplicada», ou «Aplicada com linhas por recontar» (recontam-se e
 * reenviam-se na mesma ronda).
 */
export function RondaDetailPage({ shell }: { shell: AppShell }) {
  const { id = "" } = useParams()
  // Outra ronda (ligação de uma para outra): o ecrã começa do zero.
  return <RondaScreen key={id} shell={shell} id={id} />
}

function RondaScreen({ shell, id }: { shell: AppShell; id: string }) {
  const [detail, setDetail] = useState<Detail | null>(null)
  const [review, setReview] = useState<{ review: Review; can_send: boolean } | null>(null)
  const [summary, setSummary] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [current, setCurrent] = useState<Current | null>(null)
  const [scanning, setScanning] = useState<false | "camara" | "manual">(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [recent, setRecent] = useState<RecentEntry[]>([])
  const recentSeq = useRef(0)
  const pushRecent = useCallback((fb: ScanFeedback) => setRecent((x) => [{ id: ++recentSeq.current, fb }, ...x].slice(0, 3)), [])
  /** Uma escolha correu: a resposta com as escolhas passa ao resultado (ou entra no topo, se já saiu). */
  const replaceRecent = useCallback(
    (prev: ScanFeedback, next: ScanFeedback) =>
      setRecent((x) => (x.some((e) => e.fb === prev) ? x.map((e) => (e.fb === prev ? { ...e, fb: next } : e)) : [{ id: ++recentSeq.current, fb: next }, ...x].slice(0, 3))),
    [],
  )
  /** Código desconhecido à espera de «Associar a um artigo» (`nonGtin`: não é um EAN — pergunta antes). */
  const [pendingCode, setPendingCode] = useState<PendingCode | null>(null)
  /**
   * «Associar a um artigo» (0.45.3): o botão leva o SEU código — antes só fechava
   * o leitor e contava com o código à espera, que a leitura seguinte trocava (ou
   * que já não existia, tocado na lista da página): não fazia nada. Fecha o
   * leitor e leva à pesquisa do artigo.
   */
  const [associateOpen, setAssociateOpen] = useState(false)
  const startAssociate = (code: string, nonGtin: boolean) => {
    setPendingCode({ code, nonGtin })
    setScanning(false)
    // 0.45.4: abre a janela «Associar <código> a um artigo» (pesquisa + lista) — só
    // pôr o cursor na pesquisa da página não se via no iPhone (o teclado não abre).
    setAssociateOpen(true)
  }
  /** Rever sozinho ao entrar em revisão; não logo a seguir a enviar (mostra-se o resultado). */
  const [autoReview, setAutoReview] = useState(true)
  /** O aviso «ativar o número de série»: a leitura fica à espera da resposta de quem lê. */
  const [activation, setActivation] = useState<{ ask: SerialActivationAsk; code: string; force: boolean; target: Current; resolve: (fb: ScanFeedback) => void } | null>(null)
  const [activationBusy, setActivationBusy] = useState(false)
  const [activationError, setActivationError] = useState<string | null>(null)
  /** «Substituir pela minha contagem»: o artigo à espera da confirmação. */
  const [replaceAsk, setReplaceAsk] = useState<ReviewItem | null>(null)
  const [replaceBusy, setReplaceBusy] = useState(false)
  const [replaceError, setReplaceError] = useState<string | null>(null)
  /** Um aviso de cada vez: enquanto está aberto, as leituras novas não contam. */
  const activationOpen = useRef(false)
  useEffect(() => {
    activationOpen.current = activation !== null
  }, [activation])

  const load = useCallback(
    () =>
      getRonda(shell, id)
        .then((d) => {
          setDetail(d)
          return d
        })
        .catch((e: Error) => {
          setError(e.message)
          return null
        }),
    [shell, id],
  )
  useEffect(() => {
    void load()
  }, [load])

  const ronda = detail?.check
  const working = ronda ? WORKING.includes(ronda.status) && !detail?.legacy : false
  const sending = Boolean(ronda?.result_json?.sending)
  const reading = working && !ronda?.counting_closed_at && !sending
  const reviewing = working && Boolean(ronda?.counting_closed_at) && !sending

  // Várias pessoas a ler: o ecrã atualiza-se sozinho.
  useEffect(() => {
    if (!reading || scanning) return
    const t = setInterval(() => !document.hidden && void load(), 10_000)
    return () => clearInterval(t)
  }, [reading, scanning, load])

  const runReview = useCallback(async () => {
    setReview(null)
    setSummary(false)
    try {
      setReview(await reviewRonda(shell, id))
    } catch (e) {
      setError((e as Error).message)
    }
  }, [shell, id])

  // Em revisão (por rever, ou depois de «Terminei»): compara com o Odoo de agora.
  // Só os resultados de envios da ronda (`send_no`); os de uma conferência antiga não contam.
  const hasResults = (detail?.lines ?? []).some((l) => l.send_result?.send_no)
  useEffect(() => {
    if (reviewing && autoReview && !review) void runReview()
  }, [reviewing, autoReview, review, runReview])

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError((e as Error).message)
      if ((e as RequestError).details && ((e as RequestError).details as { need_review?: boolean }).need_review) setReview(null)
    } finally {
      setBusy(null)
    }
  }

  const products = useMemo(() => new Map((detail?.products ?? []).map((p) => [p.product_id, p])), [detail])
  /** O artigo aberto agora (as escolhas correm depois, com o estado de então). */
  const currentRef = useRef(current)
  currentRef.current = current
  const latest = (target: Current): Current => (currentRef.current?.id === target.id ? { ...target, ...currentRef.current } : target)

  /**
   * A ler séries (0.42.7): a resposta com as escolhas de quem lê — «Guardar
   * como código de barras» (grava no Odoo, não conta) e «É a série» (conta,
   * ou volta a abrir o aviso de ativar o número de série).
   */
  const withChoices = (base: Pick<ScanFeedback, "tone" | "text">, code: string, target: Current, choices: SerialChoice[]): ScanFeedback => ({
    ...base,
    actions: choices.map((c) => ({
      label: CHOICE_LABEL[c],
      run: (): Promise<ScanFeedback> => {
        const t = latest(target)
        if (c === "guardar_codigo") return saveAsProductCode(code, t, choices)
        // «É a série — contar»: o «contar mesmo assim»; «É a série (ativar…)»: o aviso outra vez para este código.
        return evaluate(code, c === "serie_contar", t)
      },
    })),
  })

  /** Os pedidos de «Guardar como código de barras» (o aviso «não é um EAN» é o diálogo da app). */
  const saveDeps = (target: Current): SaveBarcodeDeps => ({
    confirmNonGtin: (code) =>
      confirmDialog(`${NON_GTIN_WARNING} Guardar ${code} como código de barras de ${target.name ?? `Artigo ${target.id}`} no Odoo? Não conta nenhuma unidade.`, {
        title: "Guardar como código de barras?",
        confirmLabel: "Guardar mesmo assim",
      }),
    removeLine: (lineId) => patchRonda(shell, id, { remove_line: lineId }),
    associate: (body) => associateCode(shell, id, body),
  })

  const savedToast = (code: string, target: Current) =>
    shell.bridge.toast({ variant: "success", title: "Código de barras gravado no Odoo", description: `${code} · ${target.name ?? `Artigo ${target.id}`}` })

  /** «Guardar como código de barras»: grava o código no artigo do Odoo, sem contar (`count: false`). */
  const saveAsProductCode = async (code: string, target: Current, choices: SerialChoice[]): Promise<ScanFeedback> => {
    if (activationOpen.current) return withChoices({ tone: "info", text: "Responda primeiro ao aviso do número de série; depois escolha o que é este código." }, code, target, choices)
    const name = target.name ?? `Artigo ${target.id}`
    const out = await saveAsBarcode(saveDeps(target), { product_id: target.id, code })
    switch (out.kind) {
      case "declined":
        return withChoices({ tone: "warn", text: `${code} não foi gravado nem contou. O que é este código?` }, code, target, choices)
      case "saved":
        savedToast(out.code, target)
        void load()
        return { tone: "ok", text: `${out.code} → ${name} · ${out.message}` }
      case "offline":
        return withChoices({ tone: "error", text: "Sem rede — não foi gravado. Tente outra vez." }, code, target, choices)
      case "refused":
        // Recusado (o artigo já tem outro código, o código é de outro artigo, é uma série…): fica só «É a série».
        return withChoices({ tone: "warn", text: `${code} não foi gravado: ${out.message}` }, code, target, withoutAssociation(choices))
    }
  }

  /**
   * «Não é série — Guardar como código de barras» (0.45.1): a série acabada de
   * contar (num artigo com séries, código que o Odoo não conhece) sai da
   * contagem e o código fica gravado no artigo, sem contar. `again` é a
   * resposta da leitura, que volta tal e qual se o aviso «não é um EAN» for cancelado.
   */
  const notSerial = async (code: string, lineId: string, target: Current, again: () => ScanFeedback): Promise<ScanFeedback> => {
    if (activationOpen.current) return { ...again(), tone: "info", text: "Responda primeiro ao aviso do número de série; depois escolha o que é este código." }
    const name = target.name ?? `Artigo ${target.id}`
    const out = await saveAsBarcode(saveDeps(target), { product_id: target.id, code, lineId })
    if (out.kind !== "declined" && out.removed) void load()
    switch (out.kind) {
      case "declined":
        return { ...again(), text: `${code} continua contado como série de ${name}.` }
      case "saved":
        savedToast(out.code, target)
        void load()
        return { tone: "ok", text: `${out.code} saiu das séries · ${out.message}` }
      case "offline":
        return out.removed
          ? withChoices({ tone: "error", text: `Sem rede — ${code} saiu das séries, mas não foi gravado. Tente outra vez.` }, code, target, ["guardar_codigo", "serie_contar"])
          : { ...again(), tone: "error", text: `Sem rede — nada mudou (${code} continua contado como série). Tente outra vez.` }
      case "refused":
        // O artigo já tem outro código, o código é de outro artigo, é uma série/lote no Odoo…: a mensagem do servidor.
        return out.removed
          ? withChoices({ tone: "warn", text: `${code} saiu das séries, mas não foi gravado: ${out.message}` }, code, target, ["serie_contar"])
          : { tone: "warn", text: `${code} continua contado como série: ${out.message}` }
    }
  }

  /** A resposta de uma série contada (ou já lida), com «Não é série — …» quando o Odoo não conhece o código. */
  const serialFeedback = (base: Pick<ScanFeedback, "tone" | "text">, r: ScanOutcome, forced: boolean): ScanFeedback => {
    if (!r.product || !r.line || !offerNotSerial(r, { forced })) return base
    const target: Current = { id: r.product.id, name: r.product.name, default_code: r.product.default_code, tracking: r.product.tracking, uom: r.product.uom }
    const lineId = r.line.id
    // Não pára o leitor nativo (a maior parte destas leituras são séries): o botão fica nas recentes.
    const fb = (): ScanFeedback => ({ ...base, action: notSerialAction(() => notSerial(r.code, lineId, latest(target), fb)) })
    return fb()
  }

  /** Uma leitura do leitor: a resposta entra nas recentes do ecrã. */
  const onCode = async (raw: string): Promise<ScanFeedback> => {
    const fb = await evaluate(raw)
    pushRecent(fb)
    return fb
  }

  /**
   * O que uma leitura é (sem a pôr nas recentes: uma escolha substitui a
   * resposta de onde veio). `serialFor`: ler o código como série desse artigo
   * sem rastreio («É uma série de …»), mesmo antes de o estado `current` mudar.
   */
  const evaluate = async (raw: string, force = false, serialFor: Current | null = null): Promise<ScanFeedback> => {
    if (notASerial(raw)) return { tone: "error", text: "É um link ou texto (ex.: o QR do fabricante) — não é código de artigo nem série." }
    if (activationOpen.current) {
      // Com o aviso aberto nada conta; a ler séries, fica com as escolhas para resolver depois de responder.
      const open = serialFor ?? currentRef.current
      const code = raw.trim().replace(/\s+/g, "").toUpperCase()
      if (open && inSerialMode(open)) {
        if (isOwnCode(code, open)) return { tone: "info", text: "É o código do artigo — leia a etiqueta com SN." }
        return withChoices({ tone: "info", text: `${code} não contou (lido com o aviso do número de série aberto). O que é este código?` }, code, open, serialModeChoices("aviso_aberto", code, open))
      }
      return { tone: "info", text: "Responda primeiro ao aviso do número de série — esta leitura não contou." }
    }
    const target = serialFor ?? currentRef.current
    const asSerial = !!target && target.tracking !== "serial" && (serialFor !== null || target.asSerial === true)
    let r
    try {
      r = await scanRonda(shell, id, {
        code: raw,
        product_id: target && (target.tracking === "serial" || asSerial) ? target.id : null,
        ...(force ? { force_ean: true } : {}),
        ...(asSerial ? { as_serial: true } : {}),
      })
    } catch (e) {
      const err = e as RequestError
      return { tone: "error", text: err.status === null ? "Sem rede — não contou. Leia outra vez." : err.message }
    }
    if (r.product) {
      const keep = asSerial && r.product.id === target?.id && r.product.tracking !== "serial"
      setCurrent({ id: r.product.id, name: r.product.name, default_code: r.product.default_code, tracking: r.product.tracking, uom: r.product.uom, ...(keep ? { asSerial: true } : {}) })
    }
    void load()
    const name = r.product?.name ?? "Artigo"
    let fb: ScanFeedback
    switch (r.result) {
      case "somado":
        fb = { tone: "ok", text: `✓ ${name} — ${r.message} (agora ${fmtQty(num(r.line?.counted_qty))})` }
        break
      case "serie":
        fb = serialFeedback({ tone: r.message === "Contada." ? "ok" : "warn", text: `Série ${r.code} → ${name}${r.message === "Contada." ? " · contada" : ` · ${r.message}`}` }, r, force)
        break
      case "ja_lida":
        fb = serialFeedback({ tone: "warn", text: r.message }, r, force)
        break
      case "artigo_com_serie":
        fb = { tone: "info", text: r.message }
        break
      case "parece_ean": {
        // O artigo com séries da resposta serve se o ecrã ainda não o tiver aberto.
        const fromScan: Current | null = r.product ? { id: r.product.id, name: r.product.name, default_code: r.product.default_code, tracking: r.product.tracking, uom: r.product.uom } : null
        const mode = target && asSerial ? { ...target, asSerial: true } : (target ?? fromScan)
        const art = mode && inSerialMode(mode) ? mode : fromScan && inSerialMode(fromScan) ? fromScan : null
        fb = art
          ? withChoices({ tone: "warn", text: `${r.code} parece um EAN, não uma série. O que é?` }, r.code, art, serialModeChoices("parece_ean", r.code, art))
          : { tone: "warn", text: r.message, action: { label: "Contar mesmo assim", run: () => evaluate(r.code, true, asSerial ? target : null) } }
        break
      }
      case "ativar_serie":
        // A leitura fica à espera do aviso: o resultado (contada / não contada) aparece no fim.
        // Outra leitura (do mesmo frame) já abriu o aviso: esta não conta e fica com as escolhas.
        fb = activationOpen.current
          ? withChoices(
              { tone: "info", text: `${r.code} não contou (lido com o aviso do número de série aberto). O que é este código?` },
              r.code,
              { ...target!, asSerial: true },
              serialModeChoices("aviso_aberto", r.code, { ...target!, asSerial: true }),
            )
          : r.activation
          ? await new Promise<ScanFeedback>((resolve) => {
              setActivationError(null)
              activationOpen.current = true
              setActivation({ ask: r.activation!, code: r.code, force, target: { ...target!, asSerial: true }, resolve })
            })
          : { tone: "warn", text: r.message }
        break
      case "desconhecido":
        if (r.can_associate && r.non_gtin) {
          // Não é um EAN: pode ser o código das caixas (associa-se, com aviso) ou a série desta caixa.
          setPendingCode({ code: r.code, nonGtin: true })
          const art = current && current.tracking === "none" ? current : null
          fb = art
            ? { tone: "warn", text: r.message, action: { label: `É uma série de ${art.name ?? "este artigo"}`, run: () => evaluate(r.code, false, art) } }
            : { tone: "warn", text: `Código ${r.code} desconhecido (não é um EAN) · procure o artigo`, action: { label: "Associar a um artigo", run: () => startAssociate(r.code, true) } }
        } else if (r.can_associate) {
          setPendingCode({ code: r.code, nonGtin: false })
          fb = { tone: "warn", text: `Código ${r.code} desconhecido · Associar a um artigo (grava o EAN no Odoo)`, action: { label: "Associar a um artigo", run: () => startAssociate(r.code, false) } }
        } else if (current && current.tracking === "none") {
          // Com um artigo sem rastreio aberto, o código pode ser a série dele.
          const art = current
          fb = { tone: "warn", text: r.message, action: { label: `É uma série de ${art.name ?? "este artigo"}`, run: () => evaluate(r.code, false, art) } }
        } else fb = { tone: "warn", text: r.message }
        break
      default:
        fb = { tone: "warn", text: r.message }
    }
    return fb
  }

  const answerActivation = async (confirm: boolean) => {
    const a = activation
    if (!a) return
    const name = a.ask.product_name ?? "Artigo"
    if (!confirm) {
      setActivation(null)
      // A leitura que esperava pelo aviso mostra-o (e entra nas recentes), com as escolhas:
      // «É a série» volta a abrir o aviso; «É o código do artigo» grava-o sem contar.
      a.resolve(
        withChoices(
          { tone: "warn", text: `${a.code} não contou — ${name} continua sem número de série no Odoo. O que é este código?` },
          a.code,
          a.target,
          serialModeChoices("ativacao_cancelada", a.code, a.target),
        ),
      )
      return
    }
    setActivationBusy(true)
    setActivationError(null)
    try {
      const r = await activateRondaSerial(shell, id, { product_id: a.ask.product_id, code: a.code, ...(a.force ? { force_ean: true } : {}) })
      if (r.product) setCurrent({ id: r.product.id, name: r.product.name, default_code: r.product.default_code, tracking: r.product.tracking, uom: r.product.uom })
      const fb: ScanFeedback =
        r.result === "serie"
          ? { tone: r.message === "Contada." ? "ok" : "warn", text: `${name} passou a ter número de série · série ${r.code} ${r.message === "Contada." ? "contada" : `· ${r.message}`}` }
          : { tone: "warn", text: r.message }
      if (r.activated && !r.activated.already) shell.bridge.toast({ variant: "success", title: "Número de série ativado no Odoo", description: name })
      setActivation(null)
      a.resolve(fb)
      void load()
    } catch (e) {
      const err = e as RequestError
      setActivationError(err.status === null ? "Sem resposta — pode ter sido ativado; repita para confirmar." : err.message)
    } finally {
      setActivationBusy(false)
    }
  }

  /** Confirmado: guarda a decisão no servidor e mostra o «Rever» refeito. */
  const answerReplace = async () => {
    const item = replaceAsk
    const p = item?.pending?.[0]
    if (!item || !p) return
    setReplaceBusy(true)
    setReplaceError(null)
    try {
      const r = await confirmReplacePending(shell, id, { product_id: item.product_id, user_id: p.user_id, inventory_quantity: p.inventory_quantity })
      setReview(r)
      setSummary(false)
      setReplaceAsk(null)
    } catch (e) {
      const err = e as RequestError
      if ((err.details as { need_review?: boolean } | undefined)?.need_review) {
        // A pendente mudou no Odoo: o «Rever» compara outra vez e mostra a nova.
        setReplaceAsk(null)
        setError(err.message)
        void runReview()
      } else setReplaceError(err.status === null ? "Sem rede — nada foi guardado. Tente outra vez." : err.message)
    } finally {
      setReplaceBusy(false)
    }
  }

  if (!detail || !ronda) return <div className="p-4 text-base text-muted-foreground">{error ?? "A carregar…"}</div>
  const status = RONDA_STATUS[ronda.status]
  const badge = reviewing && !hasResults ? { label: "Em revisão", variant: "warning" as const } : status

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start gap-2">
        <Link to="/armazem/ronda" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full hover:bg-muted" aria-label="Voltar">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1 pt-1">
          <h1 className="text-lg font-semibold leading-snug">
            {summary ? "Enviar para o Odoo" : reviewing && !hasResults ? "Rever" : "Ronda"} · {ronda.location_name ?? "localização"}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <Badge variant={badge.variant} className="text-sm">
              {badge.label}
            </Badge>
            <span>{ronda.mode === "completa" ? "Tudo da localização" : "Só o que eu ler"}</span>
            {ronda.blind && <span>· cega</span>}
            {ronda.created_by_name && <span>· {ronda.created_by_name}</span>}
          </div>
        </div>
      </div>

      {detail.legacy && <LegacyView detail={detail} />}

      {ronda.status === "por_rever" && !hasResults && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="flex gap-2 p-3 text-sm text-amber-900">
            <Smartphone className="mt-0.5 h-5 w-5 shrink-0" />
            Contagem da carrinha feita no telemóvel{ronda.counting_closed_at ? ` (${hhmm(ronda.counting_closed_at)})` : ""}. Reveja e envie — ou «Voltar a ler» para acertar.
          </CardContent>
        </Card>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {sending && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="space-y-2 p-3 text-sm text-amber-900">
            <p>O envio ficou a meio (sem ligação ao Odoo). «Enviar» continua de onde ficou — nada se escreve duas vezes.</p>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => run("send", async () => setDetail(await sendRonda(shell, id, ronda.result_json?.review?.at ?? "")))}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy === "send" && <Loader2 className="h-5 w-5 animate-spin" />} Enviar (continuar)
            </button>
            {detail.can_manage ? (
              <button
                type="button"
                disabled={busy !== null}
                onClick={() =>
                  run("discard", async () => {
                    if (!(await confirmDialog("Desistir deste envio? O que já foi aplicado no Odoo fica; o resto volta a rever-se e a enviar."))) return
                    setReview(null)
                    setAutoReview(false)
                    setDetail(await patchRonda(shell, id, { discard_sending: true }))
                  })
                }
                className="h-11 w-full rounded-xl border border-amber-300 bg-background font-medium disabled:opacity-50"
              >
                Desistir do envio
              </button>
            ) : (
              <p className="text-xs">Se continuar a falhar, um responsável do armazém pode desistir do envio.</p>
            )}
          </CardContent>
        </Card>
      )}

      {ronda.status === "fechada_com_pendentes" && ronda.result_json?.closed && (
        <Card>
          <CardContent className="space-y-1 p-3 text-sm">
            <p className="font-semibold">Fechada com pendentes{ronda.result_json.closed.by_name ? ` · ${ronda.result_json.closed.by_name}` : ""}</p>
            <p className="text-muted-foreground">{ronda.result_json.closed.reason}</p>
            {ronda.result_json.closed.pending.length > 0 && (
              <ul className="list-disc pl-5">
                {ronda.result_json.closed.pending.map((p, i) => (
                  <li key={i}>
                    {p.product_name ?? `Artigo ${p.product_id}`}
                    {p.lot_name ? ` · ${p.lot_name}` : ""} — {lineResultLabel(p.status).label}
                    {p.message ? `: ${p.message}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {hasResults && <ResultView detail={detail} busy={busy} onRecount={(pid) => run("recount", async () => setDetail(await patchRonda(shell, id, { recount_product: pid })))} onReviewAgain={() => {
            setAutoReview(true)
            void runReview()
          }} canReviewAgain={reviewing && !review && !autoReview} />}

      {ronda.status === "aplicada_parcial" && !sending && detail.can_manage && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() =>
            run("close-pending", async () => {
              if (!(await confirmDialog("Fechar esta ronda com as linhas por resolver? O que foi aplicado fica; o resto trata-se no Odoo ou noutra ronda."))) return
              setDetail(await patchRonda(shell, id, { close_with_pending: true }))
            })
          }
          className="h-11 w-full rounded-xl border text-sm font-medium active:bg-muted disabled:opacity-50"
        >
          Fechar com pendentes
        </button>
      )}

      {reading && (
        <ReadingView
          shell={shell}
          detail={detail}
          current={current}
          setCurrent={setCurrent}
          recent={recent}
          onRecentChange={replaceRecent}
          pendingCode={pendingCode}
          clearPending={() => setPendingCode(null)}
          associateOpen={associateOpen}
          onAssociateClose={() => setAssociateOpen(false)}
          onAssociated={(fb) => {
            pushRecent(fb)
            setPendingCode(null)
            setAssociateOpen(false)
            void load()
          }}
          onChanged={setDetail}
          onError={setError}
          onFeedback={pushRecent}
          onScanSerials={() => setScanning("camara")}
        />
      )}

      {activation && (
        <SerialActivationDialog
          ask={activation.ask}
          context="ronda"
          busy={activationBusy}
          error={activationError}
          onConfirm={() => void answerActivation(true)}
          onDecline={() => void answerActivation(false)}
        />
      )}

      {reviewing && review && !summary && (
        <ReviewView
          review={review.review}
          products={products}
          busy={busy}
          canSend={review.can_send}
          onReplace={(item) => {
            setReplaceError(null)
            setReplaceAsk(item)
          }}
          onRecount={(pid) =>
            run("recount", async () => {
              setReview(null)
              setDetail(await patchRonda(shell, id, { recount_product: pid }))
            })
          }
          onContinue={() => setSummary(true)}
          onBack={() =>
            run("reopen", async () => {
              setReview(null)
              setDetail(await patchRonda(shell, id, { reopen_counting: true }))
            })
          }
          onRefresh={() => void runReview()}
        />
      )}
      {replaceAsk && (
        <ConfirmDialog
          title="Substituir a contagem pendente?"
          confirmLabel="Substituir pela minha contagem"
          busy={replaceBusy}
          error={replaceError}
          onConfirm={() => void answerReplace()}
          onCancel={() => setReplaceAsk(null)}
        >
          <p>
            <span className="font-semibold">{replaceAsk.product_name ?? `Artigo ${replaceAsk.product_id}`}</span>
          </p>
          <p>{replaceConfirmText(replaceAsk)}</p>
          <p className="text-sm text-muted-foreground">Só se aplica ao enviar. Se a contagem pendente mudar no Odoo entretanto, a linha não se aplica e volta por resolver.</p>
        </ConfirmDialog>
      )}
      {reviewing && !review && autoReview && <p className="text-sm text-muted-foreground">A comparar com o Odoo…</p>}

      {reviewing && review && summary && (
        <SummaryView
          review={review.review}
          canSend={review.can_send}
          busy={busy}
          onBack={() => setSummary(false)}
          onSend={() =>
            run("send", async () => {
              const d = await sendRonda(shell, id, review.review.at)
              setAutoReview(false)
              setDetail(d)
              setReview(null)
              setSummary(false)
              const ls = d.check.result_json?.last_send
              shell.bridge.toast({
                variant: d.check.status === "aplicada" ? "success" : "info",
                title: d.check.status === "aplicada" ? "Aplicada no Odoo" : "Enviada — há linhas por recontar",
                description: ls ? `${ls.applied} acerto(s) · ${ls.assigned} série(s) atribuída(s)` : undefined,
              })
            })
          }
        />
      )}

      {working && !sending && !hasResults && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() =>
            run("abandon", async () => {
              if (!(await confirmDialog("Abandonar esta ronda? Nada é escrito no Odoo.", { destructive: true, confirmLabel: "Abandonar" }))) return
              setDetail(await patchRonda(shell, id, { abandon: true }))
            })
          }
          className="h-12 w-full rounded-xl text-base text-red-700 hover:bg-red-50"
        >
          Abandonar a ronda
        </button>
      )}
      {ronda.status === "abandonada" && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">Ronda abandonada. Nada foi escrito no Odoo.</CardContent>
        </Card>
      )}

      {reading && (
        <>
          <ActionBarSpacer />
          <ActionBar>
            <button
              type="button"
              disabled={busy !== null || (detail.products.length === 0 && ronda.mode !== "completa")}
              onClick={() =>
                run("close", async () => {
                  setDetail(await patchRonda(shell, id, { close_counting: true }))
                  setReview(null)
                  setAutoReview(true)
                })
              }
              className="h-14 shrink-0 rounded-xl border px-3 text-base font-medium active:bg-muted disabled:opacity-50"
            >
              {busy === "close" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Terminei — rever"}
            </button>
            <button
              type="button"
              onClick={() => setScanning("manual")}
              aria-label="Escrever o código à mão"
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border active:bg-muted"
            >
              <Keyboard className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setScanning("camara")}
              className="flex h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground active:opacity-90"
            >
              <ScanBarcode className="h-6 w-6" /> Ler código
            </button>
          </ActionBar>
        </>
      )}

      {scanning && (
        <Scanner
          title={current ? (current.name ?? "Artigo") : `Ronda · ${ronda.location_name ?? ""}`}
          hint={
            current?.tracking === "serial" || current?.asSerial
              ? "Leia a etiqueta com o nº de série de cada caixa"
              : "EAN, caixa, etiqueta com a referência ou série"
          }
          counter={
            current
              ? current.tracking === "serial" || current.asSerial
                ? `${products.get(current.id)?.serials.length ?? 0} séries`
                : `${fmtQty(products.get(current.id)?.counted_qty ?? 0)} ${current.uom ?? ""}`
              : undefined
          }
          onCode={(code) => onCode(code)}
          onFeedbackChange={replaceRecent}
          startManual={scanning === "manual"}
          onClose={() => {
            setScanning(false)
            void load()
          }}
        />
      )}
    </div>
  )
}

// ─── Ler ────────────────────────────────────────────────────────────────────

/**
 * «Associar <código> a um artigo» (0.45.4): pesquisa (todos os artigos com
 * stock) e a lista com «Associar» em cada um. Um artigo sem controlo de stock
 * aparece a cinzento, sem botão. O aviso «não é um EAN» continua no
 * `ConfirmDialog` (por cima desta janela).
 */
function AssociateSheet({
  shell,
  rondaId,
  code,
  nonGtin,
  busyId,
  error,
  onPick,
  onClose,
  onError,
}: {
  shell: AppShell
  rondaId: string
  code: string
  nonGtin: boolean
  busyId: number | null
  error: string | null
  onPick: (p: PickProduct) => void
  onClose: () => void
  onError: (e: string | null) => void
}) {
  const [q, setQ] = useState("")
  const [items, setItems] = useState<PickProduct[] | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    const t = q.trim()
    if (t.length < 2) return setItems(null)
    setLoading(true)
    const timer = setTimeout(() => {
      searchRondaProducts(shell, rondaId, t, true)
        .then(setItems)
        .catch((e: Error) => onError(e.message))
        .finally(() => setLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [shell, rondaId, q, onError])
  return (
    <BottomSheet open onClose={onClose} title={`Associar ${code} a um artigo`}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {nonGtin
            ? "Não é um EAN: associe só se este código estiver em todas as caixas do artigo (não se for o número de série desta caixa). "
            : ""}
          O código fica gravado como código de barras do artigo no Odoo.
        </p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nome ou referência do artigo"
            aria-label="Procurar o artigo"
            className="h-12 w-full rounded-xl border-2 border-primary/60 bg-background pl-10 pr-10 text-base"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>
        {error && <p className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</p>}
        {items === null && <p className="text-sm text-muted-foreground">Escreva pelo menos 2 letras do nome ou da referência.</p>}
        {items !== null && items.length === 0 && <p className="text-sm text-muted-foreground">Nenhum artigo encontrado.</p>}
        <div className="space-y-2">
          {(items ?? []).map((p) => {
            const blocked = p.storable === false
            const hasBarcode = !!p.barcode
            return (
              <div key={p.id} className={`flex items-center gap-3 rounded-xl border p-3 ${blocked ? "border-dashed bg-muted/40 text-muted-foreground" : "bg-card"}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{p.name ?? `Artigo ${p.id}`}</div>
                  <div className="break-words text-sm text-muted-foreground [overflow-wrap:anywhere]">
                    {[p.default_code, p.tracking === "serial" ? "com séries" : null, hasBarcode ? `já tem o código ${p.barcode}` : null, blocked ? "sem controlo de stock" : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                {!blocked && !hasBarcode && (
                  <button
                    type="button"
                    disabled={busyId !== null}
                    onClick={() => onPick(p)}
                    className="flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    {busyId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Associar
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </BottomSheet>
  )
}

function ReadingView({
  shell,
  detail,
  current,
  setCurrent,
  recent,
  onRecentChange,
  pendingCode,
  clearPending,
  associateOpen,
  onAssociateClose,
  onAssociated,
  onChanged,
  onError,
  onFeedback,
  onScanSerials,
}: {
  shell: AppShell
  detail: Detail
  current: Current | null
  setCurrent: (c: Current | null) => void
  recent: RecentEntry[]
  onRecentChange: (prev: ScanFeedback, next: ScanFeedback) => void
  pendingCode: PendingCode | null
  clearPending: () => void
  /** «Associar a um artigo» tocado: a janela com a pesquisa (0.45.4). */
  associateOpen: boolean
  onAssociateClose: () => void
  onAssociated: (fb: ScanFeedback) => void
  onChanged: (d: Detail) => void
  onError: (e: string | null) => void
  onFeedback: (fb: ScanFeedback) => void
  onScanSerials: () => void
}) {
  const id = detail.check.id
  const [query, setQuery] = useState("")
  const [all, setAll] = useState(false)
  const [results, setResults] = useState<PickProduct[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [associating, setAssociating] = useState<number | null>(null)
  /** Código que não é EAN: o aviso antes de associar. */
  const [askNonGtin, setAskNonGtin] = useState<PickProduct | null>(null)
  /** «Criar artigo com stock (arquiva este)» (0.44.0): o artigo à espera da confirmação. */
  const [replaceAsk, setReplaceAsk] = useState<PickProduct | null>(null)
  const [replaceBusy, setReplaceBusy] = useState(false)
  const [replaceError, setReplaceError] = useState<string | null>(null)

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) return setResults(null)
    setSearching(true)
    const t = setTimeout(() => {
      searchRondaProducts(shell, id, q, all || pendingCode !== null)
        .then(setResults)
        .catch((e: Error) => onError(e.message))
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(t)
  }, [shell, id, query, all, pendingCode, onError])

  const pick = (p: { id: number; name: string | null; default_code: string | null; tracking: string | null; uom?: string | null; odoo_qty?: number; odoo_reserved?: number }) => {
    setCurrent({ id: p.id, name: p.name, default_code: p.default_code, tracking: p.tracking, uom: p.uom ?? null, odooQty: p.odoo_qty, odooReserved: p.odoo_reserved })
    setQuery("")
    setResults(null)
  }

  /** `confirmed`: o aviso «não é um EAN» foi aceite (só para códigos que não são EAN). */
  const [associateError, setAssociateError] = useState<string | null>(null)
  const associate = async (p: PickProduct, confirmed = false) => {
    if (!pendingCode) return
    setAssociateError(null)
    if (pendingCode.nonGtin && !confirmed) return setAskNonGtin(p)
    setAskNonGtin(null)
    const code = pendingCode.code
    setAssociating(p.id)
    onError(null)
    try {
      const r = await associateCode(shell, id, { product_id: p.id, code, ...(pendingCode.nonGtin ? { allow_non_gtin: true } : {}) })
      shell.bridge.toast({ variant: "success", title: "Código gravado no Odoo", description: `${code} · ${p.name ?? `Artigo ${p.id}`}` })
      if (r.product) pick({ ...r.product })
      onAssociated({ tone: r.result === "somado" ? "ok" : "warn", text: `${code} → ${p.name ?? "artigo"} · ${r.message}` })
    } catch (e) {
      onError((e as Error).message)
      // A janela «Associar» tapa o aviso da página: mostra-o também lá dentro.
      setAssociateError((e as Error).message)
    } finally {
      setAssociating(null)
    }
  }

  const replace = async (p: PickProduct) => {
    setReplaceBusy(true)
    setReplaceError(null)
    try {
      const out = await replaceRondaProduct(shell, id, replaceRequestFor(p))
      setReplaceAsk(null)
      shell.bridge.toast({ variant: "success", title: REPLACE_TOAST, description: out.product.name ?? undefined })
      for (const fb of replaceFeedback(out)) onFeedback(fb)
      pick(out.product)
    } catch (e) {
      // 409 (entregas/receções em curso, o artigo mudou), 403 (grupos no Odoo), 422 (variantes): a mensagem do host, no aviso.
      setReplaceError((e as Error).message)
    } finally {
      setReplaceBusy(false)
    }
  }

  const summary = current ? detail.products.find((p) => p.product_id === current.id) : undefined
  const TONE: Record<string, string> = { ok: "bg-emerald-50 text-emerald-800", warn: "bg-amber-50 text-amber-900", error: "bg-red-50 text-red-800", info: "bg-sky-50 text-sky-800" }

  return (
    <div className="space-y-4">
      {detail.hidden_odoo && <p className="rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">Contagem cega: conte o que vê. O que o Odoo tem aparece no «Rever».</p>}
      {recent.map((r) => (
        <RecentFeedback key={r.id} fb={r.fb} toneClass={TONE} onChange={onRecentChange} />
      ))}

      {pendingCode && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <Link2 className="mt-0.5 h-5 w-5 shrink-0" />
          {pendingCode.nonGtin ? (
            <p className="flex-1">
              Código <span className="break-all font-mono font-semibold">{pendingCode.code}</span> desconhecido e <strong>não é um EAN</strong>. Se estiver em todas as caixas
              deste artigo, procure o artigo e toque em «Associar e contar». Se for o número de série desta caixa, não o associe.
            </p>
          ) : (
            <p className="flex-1">
              Código <span className="font-mono font-semibold">{pendingCode.code}</span> desconhecido. Procure o artigo e toque em «Associar e contar» — o código fica
              gravado como EAN no Odoo.
            </p>
          )}
          <button type="button" onClick={clearPending} aria-label="Cancelar" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-amber-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {associateOpen && pendingCode && (
        <AssociateSheet
          shell={shell}
          rondaId={id}
          code={pendingCode.code}
          nonGtin={pendingCode.nonGtin}
          busyId={associating}
          error={associateError}
          onPick={(p) => void associate(p)}
          onClose={onAssociateClose}
          onError={onError}
        />
      )}

      {askNonGtin && pendingCode && (
        <ConfirmDialog
          title="Este código não é um EAN"
          confirmLabel="Associar mesmo assim"
          busy={associating !== null}
          onConfirm={() => void associate(askNonGtin, true)}
          onCancel={() => setAskNonGtin(null)}
        >
          <p>
            <span className="break-all font-mono font-semibold">{pendingCode.code}</span> não é um EAN. Se for o número de série desta caixa (muda de caixa para caixa), não o
            associe — procure o artigo e conte-o.
          </p>
          <p>
            Associar mesmo assim a <span className="font-semibold">{askNonGtin.name ?? `Artigo ${askNonGtin.id}`}</span>? Fica gravado como código de barras do artigo no Odoo.
          </p>
        </ConfirmDialog>
      )}

      {replaceAsk && (
        <ConfirmDialog
          title="Criar artigo com stock e arquivar este?"
          confirmLabel="Criar artigo com stock"
          busy={replaceBusy}
          error={replaceError}
          onConfirm={() => void replace(replaceAsk)}
          onCancel={() => {
            setReplaceAsk(null)
            setReplaceError(null)
          }}
        >
          <p>
            <span className="font-semibold">{replaceAsk.odoo_name ?? bareName(replaceAsk.name, replaceAsk.default_code) ?? `Artigo ${replaceAsk.id}`}</span>
            {replaceAsk.default_code ? <> · <span className="break-all font-mono">{replaceAsk.default_code}</span></> : null}
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            <li>
              Cria-se no Odoo um artigo novo com a mesma referência, nome, categoria, preços, impostos, fornecedores e código de barras, com «Rastrear
              inventário» ligado.
            </li>
            <li>Este fica arquivado: o histórico (vendas, compras, movimentos) fica nele.</li>
            <li>O novo começa com stock 0 — contá-lo nesta ronda acerta-o.</li>
            <li>Recusa-se se houver entregas ou receções em curso com este artigo.</li>
          </ul>
        </ConfirmDialog>
      )}

      <div className="space-y-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            id="ronda-procurar-artigo"
            placeholder="Procurar por nome ou referência"
            className="h-12 w-full rounded-xl border-2 border-primary/60 bg-background pl-10 pr-3 text-base"
            aria-label="Procurar artigo"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>
        {!pendingCode && (
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <input type="checkbox" className="h-5 w-5" checked={all} onChange={(e) => setAll(e.target.checked)} />
            <span>
              Procurar em todos os artigos <span className="text-muted-foreground">· não só os que o Odoo tem aqui</span>
            </span>
          </label>
        )}
        {results && (
          <div className="space-y-2">
            {results.length === 0 && <p className="text-sm text-muted-foreground">Nenhum artigo encontrado.</p>}
            {results.map((p) =>
              p.storable === false ? (
                <div key={p.id} className="rounded-xl border border-dashed bg-muted/40 p-3 text-muted-foreground">
                  <div className="font-medium">{p.name ?? `Artigo ${p.id}`}</div>
                  <div className="break-words text-sm [overflow-wrap:anywhere]">{p.default_code}</div>
                  <p className="mt-1 text-sm">
                    Sem controlo de stock no Odoo («Rastrear inventário» desligado): não se conta na ronda. Num artigo que já teve movimentos o Odoo não deixa
                    ligar — pode criar um artigo novo com stock, com a mesma referência (este fica arquivado).
                  </p>
                  {canReplaceProduct(p) && (
                    <button
                      type="button"
                      disabled={replaceBusy}
                      onClick={() => {
                        setReplaceError(null)
                        setReplaceAsk(p)
                      }}
                      className="mt-2 flex min-h-12 w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-background px-3 text-sm font-medium text-foreground disabled:opacity-50"
                    >
                      <PackagePlus className="h-4 w-4" /> Criar artigo com stock (arquiva este)
                    </button>
                  )}
                </div>
              ) : (
              <div key={p.id} className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <button type="button" onClick={() => pick(p)} className="flex min-h-14 min-w-0 flex-1 items-center gap-3 rounded-xl border bg-card p-3 text-left active:bg-muted">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{p.name ?? `Artigo ${p.id}`}</div>
                    <div className="break-words text-sm text-muted-foreground [overflow-wrap:anywhere]">
                      {[p.default_code, p.tracking === "serial" ? "com séries" : null, p.barcode ? `EAN ${p.barcode}` : "sem EAN", p.odoo_qty != null ? `Odoo ${fmtQty(p.odoo_qty)}` : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Plus className="h-5 w-5 text-muted-foreground" />
                </button>
                {pendingCode && !p.barcode && (
                  <button
                    type="button"
                    disabled={associating !== null}
                    onClick={() => void associate(p)}
                    className="flex min-h-12 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 text-sm font-medium text-amber-900 disabled:opacity-50"
                  >
                    {associating === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Associar e contar
                  </button>
                )}
              </div>
              ),
            )}
          </div>
        )}
      </div>

      {current && (
        <CurrentProduct
          shell={shell}
          rondaId={id}
          current={current}
          summary={summary}
          hidden={detail.hidden_odoo}
          onClose={() => setCurrent(null)}
          onChanged={onChanged}
          onError={onError}
          onScanSerials={onScanSerials}
          onSerialMode={(on) => {
            setCurrent({ ...current, asSerial: on || undefined })
            if (on) onScanSerials()
          }}
        />
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Contado · {detail.products.length} artigo(s)</h2>
        {detail.products.length === 0 && <p className="text-sm text-muted-foreground">Ainda não se leu nada. Leia um código ou procure o artigo.</p>}
        {detail.products.map((p) => (
          <button
            key={p.product_id}
            type="button"
            onClick={() => pick({ id: p.product_id, name: p.product_name, default_code: p.default_code, tracking: p.tracking, uom: p.uom })}
            className={`flex min-h-14 w-full items-center gap-3 rounded-xl border bg-card px-3 py-2 text-left active:bg-muted ${current?.id === p.product_id ? "border-primary" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium">{p.product_name ?? `Artigo ${p.product_id}`}</div>
              <div className="text-sm text-muted-foreground">
                {[p.default_code, p.tracking === "serial" ? "com séries" : null, p.odoo_qty !== null ? `Odoo ${fmtQty(p.odoo_qty)}` : null].filter(Boolean).join(" · ")}
              </div>
              {p.serials.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.serials.slice(0, 6).map((s) => (
                    <span key={s.line_id} className="rounded bg-muted px-1.5 font-mono text-xs">
                      {s.lot_name}
                    </span>
                  ))}
                  {p.serials.length > 6 && <span className="text-xs text-muted-foreground">+{p.serials.length - 6}</span>}
                </div>
              )}
            </div>
            <div className="text-right">
              <span className="text-xl font-semibold tabular-nums">{fmtQty(p.counted_qty)}</span>
              <span className="ml-1 text-sm text-muted-foreground">{p.tracking === "serial" ? "séries" : (p.uom ?? "")}</span>
              {p.state !== "por_enviar" && <ProductStateChip state={p.state} />}
            </div>
          </button>
        ))}
      </section>
      <p className="text-xs text-muted-foreground">
        Um só campo: EAN do fornecedor, EAN da caixa, etiqueta com a referência do Odoo, código do artigo no fornecedor, ou uma série (a app descobre o artigo da série). Ler a referência de um
        artigo com séries pede «leia a etiqueta com SN» — nunca conta como série. Sem câmara: escrever a quantidade.
      </p>
    </div>
  )
}

/** Uma resposta recente no ecrã, com as escolhas (lado a lado; partem linha no telemóvel). */
function RecentFeedback({ fb, toneClass, onChange }: { fb: ScanFeedback; toneClass: Record<string, string>; onChange: (prev: ScanFeedback, next: ScanFeedback) => void }) {
  const [running, setRunning] = useState<ScanFeedback | null>(null)
  const shown = running ?? fb
  return (
    <div className={`rounded-xl px-3 py-2 text-sm ${toneClass[shown.tone] ?? ""}`}>
      <div className="break-words [overflow-wrap:anywhere]">{shown.text}</div>
      {!running && (
        <FeedbackActions
          feedback={fb}
          tone="light"
          onResult={(next, phase) => {
            if (phase === "running") return setRunning(next)
            setRunning(null)
            if (phase === "result") onChange(fb, next)
          }}
        />
      )}
    </div>
  )
}

function ProductStateChip({ state }: { state: RondaProduct["state"] }) {
  const map = {
    aplicado: "bg-emerald-50 text-emerald-800 border-emerald-200",
    por_recontar: "bg-amber-50 text-amber-900 border-amber-300",
    por_resolver: "bg-red-50 text-red-800 border-red-200",
    por_enviar: "",
  }
  const label = { aplicado: "Aplicado", por_recontar: "Por recontar", por_resolver: "Por resolver", por_enviar: "" }[state]
  return <span className={`mt-1 block rounded-full border px-2 text-xs ${map[state]}`}>{label}</span>
}

/** O artigo escolhido: +/− e a quantidade (absoluta), ou as séries lidas. */
function CurrentProduct({
  shell,
  rondaId,
  current,
  summary,
  hidden,
  onClose,
  onChanged,
  onError,
  onScanSerials,
  onSerialMode,
}: {
  shell: AppShell
  rondaId: string
  current: Current
  summary: RondaProduct | undefined
  hidden: boolean
  onClose: () => void
  onChanged: (d: Detail) => void
  onError: (e: string | null) => void
  onScanSerials: () => void
  /** Artigo sem rastreio: «Ler séries» (liga) / «Contar em quantidade» (desliga). */
  onSerialMode: (on: boolean) => void
}) {
  const saved = summary?.counted_qty ?? 0
  // Já contado: o que a ronda tem; ainda por contar: o que veio da pesquisa.
  const odooQty = summary?.odoo_qty ?? current.odooQty ?? null
  const odooReserved = summary ? null : (current.odooReserved ?? null)
  const counted = summary !== undefined
  const [value, setValue] = useState(String(saved))
  const [saving, setSaving] = useState(false)
  const card = useRef<HTMLDivElement>(null)
  useEffect(() => {
    card.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [current.id])
  useEffect(() => {
    setValue(String(saved))
  }, [saved, current.id])
  const dirty = Number(value.replace(",", ".")) !== saved

  const save = async (qty: number) => {
    if (!Number.isFinite(qty) || qty < 0) return onError("Quantidade inválida.")
    setSaving(true)
    onError(null)
    try {
      onChanged(await patchRonda(shell, rondaId, { set_qty: { product_id: current.id, qty } }))
    } catch (e) {
      onError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }
  const step = (d: number) => setValue((v) => String(Math.max(0, Math.round((Number(v.replace(",", ".")) + d) * 1000) / 1000)))

  return (
    <Card ref={card} className="scroll-mt-4 border-primary/40">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold">{current.name ?? `Artigo ${current.id}`}</div>
            <div className="text-sm text-muted-foreground">
              {[current.default_code, current.tracking === "serial" ? "com séries — leia cada SN" : current.uom].filter(Boolean).join(" · ")}
              {!hidden && odooQty != null && ` · Odoo ${fmtQty(odooQty)}${odooReserved ? ` (${fmtQty(odooReserved)} reservado)` : ""}`}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar artigo" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        {current.tracking === "serial" ? (
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold tabular-nums">{summary?.serials.length ?? 0}</span>
              <span className="text-base text-muted-foreground">séries lidas</span>
            </div>
            <button
              type="button"
              onClick={onScanSerials}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground active:opacity-90"
            >
              <ScanBarcode className="h-6 w-6" /> Ler séries
            </button>
            {(summary?.serials ?? []).map((s) => (
              <div key={s.line_id} className="flex min-h-12 items-center gap-3 rounded-xl border bg-background px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="break-all font-mono text-base font-semibold">{s.lot_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {s.counted_by_name ?? "—"} · {hhmm(s.counted_at)}
                  </div>
                  {s.warning && <div className="text-sm text-amber-800">{s.warning}</div>}
                </div>
                <button
                  type="button"
                  aria-label={`Tirar ${s.lot_name}`}
                  onClick={() =>
                    patchRonda(shell, rondaId, { remove_line: s.line_id })
                      .then(onChanged)
                      .catch((e: Error) => onError(e.message))
                  }
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        ) : current.tracking === "lot" ? (
          <p className="text-sm text-amber-800">Artigo com rastreio por lote: confira no Odoo, lote a lote.</p>
        ) : current.asSerial ? (
          <div className="space-y-2">
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Este artigo ainda não tem número de série no Odoo. Leia a etiqueta com SN: a primeira série pergunta se o artigo passa a ter número de série.
            </p>
            <button
              type="button"
              onClick={onScanSerials}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground active:opacity-90"
            >
              <ScanBarcode className="h-6 w-6" /> Ler séries
            </button>
            <button type="button" onClick={() => onSerialMode(false)} className="h-11 w-full rounded-xl border text-sm font-medium active:bg-muted">
              Contar em quantidade
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium">Contado aqui</span>
              {!counted && <span className="text-muted-foreground">ainda não contado</span>}
            </div>
            <div className="flex items-stretch gap-2">
              <button type="button" onClick={() => step(-1)} aria-label="Menos 1" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border active:bg-muted">
                <Minus className="h-6 w-6" />
              </button>
              <input
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-label="Quantidade contada"
                className="h-14 min-w-0 flex-1 rounded-xl border bg-background text-center text-2xl font-bold tabular-nums"
              />
              <button type="button" onClick={() => step(1)} aria-label="Mais 1" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border active:bg-muted">
                <Plus className="h-6 w-6" />
              </button>
            </div>
            <button
              type="button"
              disabled={!dirty || saving}
              onClick={() => void save(Number(value.replace(",", ".")))}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-40"
            >
              {saving && <Loader2 className="h-5 w-5 animate-spin" />} {dirty ? `Gravar ${value} ${current.uom ?? ""}` : counted ? "Gravado" : "Escreva a quantidade contada"}
            </button>
            <p className="text-xs text-muted-foreground">A quantidade escrita substitui a anterior. Ler o código soma.</p>
            <button
              type="button"
              onClick={() => onSerialMode(true)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border text-sm font-medium active:bg-muted"
            >
              <ScanBarcode className="h-4 w-4" /> Tem número de série na etiqueta? Ler séries
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Rever ──────────────────────────────────────────────────────────────────

const SN = ({ s, kind }: { s: string; kind?: "new" | "miss" | "away" }) => (
  <span
    className={`rounded px-1.5 py-0.5 font-mono text-xs ${
      kind === "new" ? "bg-violet-100 text-violet-800" : kind === "miss" ? "bg-red-50 text-red-700 line-through" : kind === "away" ? "bg-amber-100 text-amber-900" : "bg-muted"
    }`}
  >
    {s}
  </span>
)

function ReviewView({
  review,
  products,
  busy,
  canSend,
  onReplace,
  onRecount,
  onContinue,
  onBack,
  onRefresh,
}: {
  review: Review
  products: Map<number, RondaProduct>
  busy: string | null
  /** Quem pode enviar a ronda é quem pode substituir uma contagem pendente. */
  canSend: boolean
  onReplace: (item: ReviewItem) => void
  onRecount: (productId: number) => void
  onContinue: () => void
  onBack: () => void
  onRefresh: () => void
}) {
  const by = (s: ReviewItem["section"]) => review.items.filter((i) => i.section === s)
  const sections: Array<{ key: ReviewItem["section"]; title: string }> = [
    { key: "por_resolver", title: "Por resolver no Odoo" },
    { key: "igual", title: "Igual ao Odoo" },
    { key: "series", title: "Séries para o stock antigo" },
    { key: "diferenca", title: "Diferenças" },
    { key: "zero", title: "Vão a zero" },
  ]
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="flex-1">Comparado com o Odoo agora ({new Date(review.at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}).</span>
        <button type="button" onClick={onRefresh} className="flex h-9 items-center gap-1 rounded-lg px-2 hover:bg-muted">
          <RotateCcw className="h-4 w-4" /> Comparar outra vez
        </button>
      </div>
      {review.items.length === 0 && <p className="text-sm text-muted-foreground">Nada a comparar.</p>}
      {sections.map(({ key, title }) => {
        const list = by(key)
        if (list.length === 0) return null
        return (
          <section key={key} className="space-y-2">
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
              {title} · {list.length}
            </h2>
            {key === "zero" && <p className="text-sm text-red-900">O Odoo tem estes artigos aqui e ninguém os leu: ficam a 0 ao enviar.</p>}
            {list.map((i) => (
              <ReviewCard
                key={i.product_id}
                item={i}
                canRecount={products.has(i.product_id)}
                canReplace={canSend && i.can_replace_pending === true}
                busy={busy}
                onRecount={() => onRecount(i.product_id)}
                onReplace={() => onReplace(i)}
              />
            ))}
          </section>
        )
      })}
      {review.lot_tracked_skipped > 0 && (
        <p className="text-sm text-muted-foreground">{review.lot_tracked_skipped} artigo(s) com rastreio por lote ficam de fora — confira-os no Odoo.</p>
      )}
      <button type="button" disabled={busy !== null} onClick={onContinue} className="flex h-14 w-full items-center justify-center rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50">
        Continuar para o resumo
      </button>
      <button type="button" disabled={busy !== null} onClick={onBack} className="h-12 w-full rounded-xl border text-base font-medium active:bg-muted disabled:opacity-50">
        Voltar a ler
      </button>
      <p className="text-xs text-muted-foreground">Antes de enviar, cada diferença pode ser recontada. Uma série que está noutro sítio no Odoo não se acerta aqui: move-se.</p>
    </div>
  )
}

function ReviewCard({
  item,
  canRecount,
  canReplace,
  busy,
  onRecount,
  onReplace,
}: {
  item: ReviewItem
  canRecount: boolean
  canReplace: boolean
  busy: string | null
  onRecount: () => void
  onReplace: () => void
}) {
  const diff = item.counted_qty - item.odoo_qty
  const tone = item.section === "zero" || item.section === "por_resolver" ? "border-red-200" : item.section === "series" ? "border-violet-200" : item.section === "diferenca" ? "border-amber-200" : ""
  return (
    <div className={`space-y-2 rounded-xl border bg-card p-3 ${tone}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium">{item.product_name ?? `Artigo ${item.product_id}`}</div>
          <div className="text-sm text-muted-foreground">{item.default_code}</div>
        </div>
        {item.section === "diferenca" && item.tracking !== "serial" && (
          <span className={`text-lg font-bold tabular-nums ${diff < 0 ? "text-red-700" : "text-sky-700"}`}>{signed(diff)}</span>
        )}
        {item.section === "igual" && <span className="rounded-full bg-emerald-50 px-2 text-xs font-semibold text-emerald-800">Certo</span>}
        {item.section === "series" && <span className="rounded-full bg-violet-50 px-2 text-xs font-semibold text-violet-800">Atribuir séries</span>}
      </div>
      {item.tracking === "serial" ? (
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            Odoo: {fmtQty(item.odoo_serials?.length ?? 0)} série(s){item.odoo_no_lot ? ` + ${fmtQty(item.odoo_no_lot)} sem série` : ""} aqui · lidas {fmtQty(item.counted_qty)}
          </p>
          {item.assign.length > 0 && (
            <div>
              <p>→ {item.assign.length} série(s) para o stock sem série (o total não muda):</p>
              <div className="flex flex-wrap gap-1">{item.assign.map((s) => <SN key={s} s={s} kind="new" />)}</div>
            </div>
          )}
          {(item.extra ?? []).length > 0 && (
            <div>
              <p>Séries a mais (entram no stock):</p>
              <div className="flex flex-wrap gap-1">{item.extra!.map((s) => <SN key={s.lot_name} s={s.lot_name} kind="new" />)}</div>
              {item.extra!.filter((s) => s.warning).map((s) => (
                <p key={s.lot_name} className="text-amber-800">
                  {s.lot_name}: {s.warning}
                </p>
              ))}
            </div>
          )}
          {(item.missing ?? []).length > 0 && (
            <div>
              <p className="text-red-800">Em falta (vão a 0):</p>
              <div className="flex flex-wrap gap-1">{item.missing!.map((s) => <SN key={s} s={s} kind="miss" />)}</div>
            </div>
          )}
          {(item.elsewhere ?? []).map((e) => (
            <p key={e.lot_name} className="text-amber-900">
              <SN s={e.lot_name} kind="away" /> está em {e.location_name ?? "outra localização"} no Odoo: mover, não ajustar.
            </p>
          ))}
          {item.lines.some((l) => l.lot_name === null && l.reason === "resto_sem_serie") && (
            <p className="text-red-800">
              Sobra {fmtQty(item.lines.find((l) => l.lot_name === null)!.expected_qty)} sem série que não se leu → {fmtQty(item.lines.find((l) => l.lot_name === null)!.counted_qty)}.
            </p>
          )}
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-1 text-center text-sm">
          <div>
            <dt className="text-muted-foreground">Contado</dt>
            <dd className="text-lg font-semibold tabular-nums">{fmtQty(item.counted_qty)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Odoo</dt>
            <dd className="text-lg font-semibold tabular-nums">{fmtQty(item.odoo_qty)}</dd>
          </div>
        </dl>
      )}
      {(item.reserved ?? 0) > 0 && <p className="text-sm text-muted-foreground">Reservado para entregas aqui: {fmtQty(item.reserved!)}</p>}
      {reviewNotices(item).map((n, i) => (
        <div key={i} className="text-sm">
          <p className={n.tone === "red" ? "text-red-800" : "text-amber-900"}>
            {n.lot_name ? <SN s={n.lot_name} kind="away" /> : null} {n.text}
          </p>
          {n.hint && <p className="text-xs text-muted-foreground">{n.hint}</p>}
        </div>
      ))}
      {(canReplace || (canRecount && item.section !== "igual")) && (
        <div className="flex flex-wrap gap-2">
          {canReplace && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={onReplace}
              className="h-10 rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm font-medium text-amber-900 active:bg-amber-100 disabled:opacity-50"
            >
              Substituir pela minha contagem
            </button>
          )}
          {canRecount && item.section !== "igual" && (
            <button type="button" disabled={busy !== null} onClick={onRecount} className="h-10 rounded-lg border px-3 text-sm font-medium active:bg-muted disabled:opacity-50">
              Voltar a contar
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Resumo e enviar ────────────────────────────────────────────────────────

function SummaryView({ review, canSend, busy, onBack, onSend }: { review: Review; canSend: boolean; busy: string | null; onBack: () => void; onSend: () => void }) {
  const assign = review.items.filter((i) => i.assign.length > 0)
  const sent = (i: ReviewItem) => (i.section === "por_resolver" ? [] : i.lines.filter((l) => l.send !== false))
  const adjust = review.items.filter((i) => sent(i).length > 0)
  const same = review.items.filter((i) => i.section === "igual")
  const blocked = review.items.filter((i) => i.section === "por_resolver" || i.lines.some((l) => l.hold))
  const adjustments = adjust.reduce((s, i) => s + sent(i).length, 0)
  return (
    <div className="space-y-3">
      <p className="text-sm">Isto é o que vai acontecer:</p>
      {assign.length > 0 && (
        <div className="rounded-xl bg-violet-50 p-3 text-sm">
          <h3 className="font-semibold">Atribuir séries — aplica já</h3>
          <p>
            {assign.reduce((s, i) => s + i.assign.length, 0)} série(s) a {assign.length} artigo(s). O total não muda; o stock antigo passa a ter número de série.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {assign.map((i) => (
              <li key={i.product_id}>
                {i.product_name} · {i.assign.length} SN
              </li>
            ))}
          </ul>
        </div>
      )}
      {adjust.length > 0 && (
        <div className="rounded-xl bg-amber-50 p-3 text-sm">
          <h3 className="font-semibold">Acertar stock — aplica já</h3>
          <p>
            {adjustments} ajuste(s) de inventário no Odoo (nome «{review.note}»). Quem leu é quem aprova — o passo «Rever» é a aprovação.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {adjust.map((i) => {
              const after = i.tracking === "serial" ? i.counted_qty : i.section === "zero" ? 0 : i.counted_qty
              const missing = i.missing?.length ? ` (falta ${i.missing.join(", ")})` : ""
              const replaces = i.replace_pending && i.lines.some((l) => l.replace_pending)
              return (
                <li key={i.product_id}>
                  {i.product_name} · {fmtQty(i.odoo_qty)} → {fmtQty(after)}
                  {missing}
                  {replaces && ` · substitui a contagem pendente de ${i.replace_pending!.user_name ?? "outra pessoa"} (${fmtQty(i.replace_pending!.inventory_quantity)} ${unitLabel(i.uom)})`}
                </li>
              )
            })}
          </ul>
        </div>
      )}
      {same.length > 0 && (
        <div className="rounded-xl bg-muted p-3 text-sm">
          <h3 className="font-semibold">Sem alterações</h3>
          <p>{same.length} artigo(s) igual(is) ao Odoo.</p>
        </div>
      )}
      {blocked.length > 0 && (
        <div className="rounded-xl border border-red-200 p-3 text-sm text-red-900">
          <h3 className="font-semibold">Ficam por resolver</h3>
          <p>{blocked.length} artigo(s) que a ronda não acerta, ou só acerta até ao reservado para entregas (ver «Rever»).</p>
        </div>
      )}
      {canSend ? (
        <button type="button" disabled={busy !== null} onClick={onSend} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50">
          {busy === "send" && <Loader2 className="h-5 w-5 animate-spin" />} Enviar
        </button>
      ) : (
        <p className="rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">Uma ronda «Tudo da localização» só a envia o responsável do armazém. Fica gravada.</p>
      )}
      <button type="button" disabled={busy !== null} onClick={onBack} className="h-12 w-full rounded-xl border text-base font-medium active:bg-muted disabled:opacity-50">
        Voltar
      </button>
      <p className="text-xs text-muted-foreground">Uma linha que mudou no Odoo entretanto (alguém mexeu no stock) não se aplica: fica «Por recontar» na ronda, nada escrito.</p>
    </div>
  )
}

// ─── Depois de enviar ───────────────────────────────────────────────────────

function ResultView({
  detail,
  busy,
  onRecount,
  onReviewAgain,
  canReviewAgain,
}: {
  detail: Detail
  busy: string | null
  onRecount: (productId: number) => void
  onReviewAgain: () => void
  canReviewAgain: boolean
}) {
  const ronda = detail.check
  const byProduct = new Map<number, RondaLine[]>()
  for (const l of detail.lines) if (l.send_result?.send_no) byProduct.set(l.product_id, [...(byProduct.get(l.product_id) ?? []), l])
  const ls = ronda.result_json?.last_send
  const outstanding = [...byProduct.entries()].filter(([, ls2]) => ls2.some((l) => !["applied", "unchanged", "assigned"].includes(l.send_result!.status)))
  const working = WORKING.includes(ronda.status)
  return (
    <section className="space-y-3">
      {(ls?.warnings ?? []).map((w, i) => (
        <p key={i} className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {w}
        </p>
      ))}
      {ronda.status === "aplicada" && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="flex items-center gap-2 p-3 text-base font-semibold text-emerald-900">
            <CheckCircle2 className="h-5 w-5" /> Aplicada{ls ? ` · ${ls.applied} acerto(s) · ${ls.assigned} série(s) atribuída(s)` : ""}
          </CardContent>
        </Card>
      )}
      {working && ronda.status !== "aplicada" && outstanding.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="space-y-1 p-3 text-sm text-amber-900">
            <p className="text-base font-semibold">{ronda.status === "aplicada_parcial" ? "Aplicada com linhas por recontar" : "Nada aplicado — linhas por recontar"}</p>
            <p>As linhas que mudaram no Odoo não se escreveram: reconte-as («Voltar a contar») ou reveja outra vez e reenvie, na mesma ronda.</p>
          </CardContent>
        </Card>
      )}
      {[...byProduct.entries()].map(([pid, lines]) => (
        <div key={pid} className="space-y-1 rounded-xl border bg-card p-3">
          <div className="font-medium">{lines[0]!.product_name ?? `Artigo ${pid}`}</div>
          {lines.map((l) => {
            const r = l.send_result!
            const lab = lineResultLabel(r.status)
            return (
              <div key={l.id} className="flex flex-wrap items-start gap-x-2 text-sm">
                <span className="min-w-0 flex-1">
                  {l.lot_name ? <span className="font-mono">{l.lot_name}</span> : "Quantidade"}
                  {r.quantity_before != null && r.quantity_after != null && r.status === "applied" && ` · ${fmtQty(r.quantity_before)} → ${fmtQty(r.quantity_after)}`}
                  {l.source === "odoo" && " · não lida"}
                  {r.replaced_pending && !(r.message ?? "").includes("Substituiu") && (
                    <span className="block text-muted-foreground">
                      Substituiu a contagem pendente de {r.replaced_pending.user_name ?? "outra pessoa"} ({fmtQty(r.replaced_pending.inventory_quantity)}).
                    </span>
                  )}
                  {r.message && <span className="block text-muted-foreground">{r.message}</span>}
                </span>
                <span className={`shrink-0 rounded-full border px-2 text-xs font-medium ${lab.cls}`}>{lab.label}</span>
              </div>
            )
          })}
          {working && outstanding.some(([p]) => p === pid) && (
            <button type="button" disabled={busy !== null} onClick={() => onRecount(pid)} className="mt-1 h-10 rounded-lg border px-3 text-sm font-medium active:bg-muted disabled:opacity-50">
              Voltar a contar
            </button>
          )}
        </div>
      ))}
      {working && canReviewAgain && outstanding.length > 0 && (
        <button type="button" disabled={busy !== null} onClick={onReviewAgain} className="flex h-12 w-full items-center justify-center rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-50">
          Rever outra vez e reenviar
        </button>
      )}
    </section>
  )
}

// ─── Conferências antigas ───────────────────────────────────────────────────

function LegacyView({ detail }: { detail: Detail }) {
  return (
    <div className="space-y-2">
      {detail.legacy_note && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-3 text-sm text-amber-900">{detail.legacy_note}</CardContent>
        </Card>
      )}
      {detail.lines.map((l) => (
        <div key={l.id} className="flex items-start gap-2 rounded-xl border bg-card p-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-medium">{l.product_name ?? `Artigo ${l.product_id}`}</div>
            {l.lot_name && <div className="font-mono">{l.lot_name}</div>}
            <div className="text-muted-foreground">
              Odoo {fmtQty(num(l.expected_qty_at_count))} → contado {fmtQty(num(l.counted_qty))}
            </div>
          </div>
          <span className="text-xs text-muted-foreground">{l.send_result?.status ?? "—"}</span>
        </div>
      ))}
    </div>
  )
}
